import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Drawer, Form, Space } from '@arco-design/web-react';
import { t } from '@bay-email/core';
import EmailEditorListingsCriteriaForm from './EmailEditorListingsCriteriaForm';

interface CriteriaConfig {
  // Listing settings
  maxListings?: number;

  // Search mode (from ListingsCriteriaForm)
  searchby?: 'list' | 'map';

  // Market criteria
  criteriaType?: 'map' | 'area';
  locationType?: 'city' | 'county' | 'state' | 'zip_code' | 'area' | 'subarea';
  city?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  area?: string;
  subarea?: string;

  // Map specific (support both naming conventions)
  address?: string;
  lat?: number;
  lng?: number;
  lon?: number; // alias for lng
  zoomLevel?: number;
  zoomlevel?: number; // alias for zoomLevel

  // Property selection (support both naming conventions)
  propertyType?: 'residential' | 'multifamily' | 'commercial' | 'land';
  propertytype?: 'residential' | 'multifamily' | 'commercial' | 'land';
  propertyStyles?: string[];
  style?: string;

  // Range filters (support both naming conventions)
  priceMin?: number;
  priceMax?: number;
  pricemin?: number;
  pricemax?: number;
  sqftMin?: number;
  sqftMax?: number;
  sq_footmin?: string | number;
  sq_footmax?: string | number;
  bedsMin?: number;
  bedsMax?: number;
  bedsmin?: string | number;
  bedsmax?: string | number;
  bathsMin?: number;
  bathsMax?: number;
  bathsmin?: string | number;
  bathsmax?: string | number;
  unitsMin?: number;
  unitsMax?: number;
  unitsmin?: string | number;
  unitsmax?: string | number;
  buildingsMin?: number;
  buildingsMax?: number;
  buildingsmin?: string | number;
  buildingsmax?: string | number;
  communityname?: string;
  schooldistrict?: string;
  acreageMin?: number;
  acreageMax?: number;
  acreagemin?: string | number;
  acreagemax?: string | number;
  yearBuilt?: number;
  year_built?: string | number;
  officeSqftMin?: number;
  officeSqftMax?: number;
  sq_footofficemin?: string | number;
  sq_footofficemax?: string | number;

  // Garage and stories
  garagemin?: string | number;
  garagemax?: string | number;
  minstories?: string | number;
  maxstories?: string | number;

  // Location details
  streetaddress?: string;

  // Features/amenities (support both naming conventions)
  golfCourse?: boolean;
  golfcourse?: string;
  waterFront?: boolean;
  waterfront?: string;
  propertyView?: boolean;
  propertyview?: string;
  pool?: string;
  basement?: string;
}

interface ListingItem {
  id: string;
  title?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  list_price?: number;
  beds?: number;
  baths?: number;
  sq_foot?: number;
  image_data?: string[];
}
interface CriteriaDrawerProps {
  visible: boolean;
  onClose: () => void;
  onOpen: () => void;
  disabled?: boolean;
  criteriaConfig: CriteriaConfig;
  onSave: (config: CriteriaConfig, maxListings: number, resultCount?: number) => void;
  maxListings: number;
  children?: React.ReactNode;
  setCriteriaResults: (results: ListingItem[]) => void;
}

export const CriteriaDrawer: React.FC<CriteriaDrawerProps> = ({
  visible,
  onClose,
  onOpen,
  disabled = false,
  criteriaConfig,
  onSave,
  maxListings,
  children,
  setCriteriaResults,
}) => {
  const [form] = Form.useForm();
  const [tempConfig, setTempConfig] = useState<CriteriaConfig>(criteriaConfig);
  const [resultCount, setResultCount] = useState<number>(0);
  const [tempMaxListings, setTempMaxListings] = useState<number>(maxListings);
  const [ loading , setLoading ] = useState<boolean>(false);

  // Sync with incoming config when drawer opens
  useEffect(() => {
    if (visible) {
      setTempConfig(criteriaConfig);
      setTempMaxListings(maxListings);
    }
  }, [visible, criteriaConfig, maxListings]);

  // Validation: Required fields are price, property type, style (for residential), size, and location
  const canSubmit = useMemo(() => {
    // Get property type (check both naming conventions)
    const propertyType = tempConfig.propertytype || tempConfig.propertyType;

    // 1. Price range (min and max must exist - any value is OK including defaults)
    const hasPriceRange =
      tempConfig.pricemin !== undefined &&
      tempConfig.pricemax !== undefined;

    // 2. Property type (must be set)
    const hasPropertyType = !!propertyType;

    // 3. Style (required for residential only - allow any non-empty value)
    const hasStyle =
      propertyType !== 'residential' ||
	  // Added `!!` to ensure the value is treated as a boolean and to avoid empty strings passing validation
      !!(tempConfig.style && (
        (typeof tempConfig.style === 'string' && tempConfig.style.length > 0) ||
        (Array.isArray(tempConfig.style) && tempConfig.style.length > 0)
      ));

    // 4. Size (at least one field must be set - defaults count as valid)
    const hasSize =
      tempConfig.bedsmin !== undefined ||
      tempConfig.bedsmax !== undefined ||
      tempConfig.bathsmin !== undefined ||
      tempConfig.bathsmax !== undefined ||
      tempConfig.sq_footmin !== undefined ||
      tempConfig.sq_footmax !== undefined ||
      tempConfig.sq_footofficemin !== undefined ||
      tempConfig.sq_footofficemax !== undefined ||
      tempConfig.acreagemin !== undefined ||
      tempConfig.acreagemax !== undefined ||
      tempConfig.buildingsmin !== undefined ||
      tempConfig.buildingsmax !== undefined ||
      tempConfig.unitsmin !== undefined ||
      tempConfig.unitsmax !== undefined ||
      tempConfig.garagemin !== undefined ||
      tempConfig.garagemax !== undefined ||
      tempConfig.minstories !== undefined ||
      tempConfig.maxstories !== undefined;

    // 5. Location (either map coordinates or at least one location field - any value counts)
    const hasLocation =
      (tempConfig.searchby === 'map' && tempConfig.lat !== undefined && tempConfig.lon !== undefined) ||
      (tempConfig.city !== undefined && tempConfig.city !== '') ||
      (tempConfig.county !== undefined && tempConfig.county !== '') ||
      (tempConfig.state !== undefined && tempConfig.state !== '') ||
      (tempConfig.zip_code !== undefined && tempConfig.zip_code !== '') ||
      (tempConfig.area !== undefined && tempConfig.area !== '') ||
      (tempConfig.subarea !== undefined && tempConfig.subarea !== '') ||
      (tempConfig.communityname !== undefined && tempConfig.communityname !== '') ||
      (tempConfig.schooldistrict !== undefined && tempConfig.schooldistrict !== '') ||
      (tempConfig.streetaddress !== undefined);

    const result = hasPriceRange && hasPropertyType && hasStyle && hasSize && hasLocation;

    return result;
  }, [tempConfig]);

  const handleSave = useCallback(() => {
    onSave(tempConfig, tempMaxListings, resultCount);
    onClose();
  }, [tempConfig, tempMaxListings, resultCount, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setTempConfig(criteriaConfig);
    setTempMaxListings(maxListings);
    setResultCount(0);
    onClose();
  }, [criteriaConfig, maxListings, onClose]);

  // Handle form data changes from the criteria form
  const handleCriteriaChange = useCallback((changes: any) => {
    setTempConfig((prev) => ({ ...prev, ...changes }));
  }, []);

  // Handle results count from the criteria form
  const handleResultsCount = useCallback((count: number) => {
    setResultCount(count || 0);
  }, []);

  return (
    <>
      <Drawer
        width={760}
        title={t('Select Listing Criteria')}
        visible={visible}
        onCancel={handleCancel}
        footer={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button onClick={handleCancel}>{t('Cancel')}</Button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Count pill */}
              <div
                style={{
                  padding: '6px 10px',
                  borderRadius: 16,
                  background: '#f5f7fa',
                  color: '#333',
                  fontSize: 13,
                }}
              >
                {`${resultCount} ${t('Listings')} ${t('match your criteria')}`}
              </div>
              <Button type='primary' onClick={handleSave} disabled={!canSubmit || loading}>
                {t('Confirm and Apply')}
              </Button>
            </div>
          </div>
        }
      >
        <EmailEditorListingsCriteriaForm
          t={t}
          formData={tempConfig}
          lookupOptions={{
            // TODO: Plug in the actual source used by the editor (confirm path/service)
          }}
          isFilterSearchLoading={false}
          defaultLat={tempConfig.lat || 40.7128}
          defaultLon={tempConfig.lng || -74.006}
          mobileView={false}
          onChange={handleCriteriaChange}
          onResultsCount={handleResultsCount}
		  setCriteriaResults={setCriteriaResults}
		  setLoading={setLoading}
		  loading={loading}
        />
      </Drawer>

      {children && (
        <div
          onClick={() => !disabled && onOpen()}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {children}
        </div>
      )}
    </>
  );
};
